import {TextArea} from '@dropbox/dig-components/text_fields';
import {atoms, Box} from '@dropbox/dig-foundations';
import {snackbarAtom} from 'atoms/snackbar';
import type {WorkstreamUpdate} from 'client';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {type KeyboardEvent, useEffect, useRef, useState} from 'react';
import styles from 'views/workstreams/detail/WorkstreamDetailsShell.module.css';
import {useWorkstreamStatus} from 'views/workstreams/hooks';

import {SummaryLine, SummaryPending, UpdateSummaryQuote} from './UpdateSummaryQuote';
import {forgetUpdateSummary, type UpdateSummaryState} from './useUpdateSummary';
import {logWorkstreamsInteraction} from './workstreamsAnalytics';

// Longest summary a person can save. Matches UPDATE_SUMMARY_MAX_CHARS in
// schemas/workstream_update.py.
export const UPDATE_SUMMARY_MAX_CHARS = 200;

/**
 * /workstreams: an update's summary, edited in place like the workstream's
 * description when `editable` (the update's own rule, canEditUpdate): the text
 * tints on hover or keyboard focus, and a click (or Enter / Space) turns it into
 * a borderless text area in the same type, caret at the end. Enter or blur
 * saves, Escape cancels, and saving it empty hands the summary back to AI. A
 * summary is one line, so new lines (pasted ones too) save as spaces. `quote` is
 * the detail page's larger line; otherwise it's the small one. Only the detail
 * page edits; cards show the summary read-only.
 */
export const EditableSummary = ({
  update,
  state,
  editable,
  quote,
}: {
  update: WorkstreamUpdate;
  state: UpdateSummaryState;
  editable: boolean;
  quote?: boolean;
}) => {
  const {editUpdateSummary} = useWorkstreamStatus();
  const setSnackbar = useSetAtom(snackbarAtom);
  const [editing, setEditing] = useState(false);
  // Shown from save until the refetched update carries it (or the save fails).
  const [saved, setSaved] = useState<UpdateSummaryState>();
  // Escape blurs the text area on its way out; that blur mustn't save.
  const skipBlurCommitRef = useRef(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => setSaved(undefined), [update.summary, update.summary_edited]);

  // Focused here, caret at the end, rather than with autoFocus: without CSS
  // field-sizing, the auto-growing TextArea renders a hidden copy that would
  // take autoFocus too and blur (saving and closing) the real one.
  useEffect(() => {
    const input = inputRef.current;
    if (!editing || !input) return;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }, [editing]);

  const shown = saved ?? state;

  const commit = async (value: string) => {
    setEditing(false);
    const summary = value.replace(/\s+/g, ' ').trim();
    if (summary === (shown.summary ?? '')) return;
    setSaved(
      summary
        ? {summary, edited: true, pending: false}
        : {summary: undefined, edited: false, pending: true}
    );
    try {
      await editUpdateSummary({id: update.id, workstreamId: update.workstream_id, summary});
      logWorkstreamsInteraction('summary_edit_saved', {
        workstream_id: update.workstream_id,
        // Saved empty: the summary goes back to AI.
        reset_to_ai: !summary,
        location: quote ? 'detail' : 'card',
      });
      // A reset must be asked for again, not served from this session's cache.
      if (!summary) forgetUpdateSummary(update.id);
    } catch {
      setSaved(undefined);
      setSnackbar({text: t('couldnt_save')});
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      // Don't let the card's Escape-to-collapse fire too.
      event.stopPropagation();
      skipBlurCommitRef.current = true;
      event.currentTarget.blur();
    }
  };

  const startEditing = () => {
    logWorkstreamsInteraction('summary_edit_opened', {
      workstream_id: update.workstream_id,
      was_edited: shown.edited,
      location: quote ? 'detail' : 'card',
    });
    setEditing(true);
  };

  if (editing) {
    // Held tinted (the hover look), which also marks the unstyled field's focus.
    return (
      <Box className={styles.heroSummaryDisplay} backgroundColor="Background Subtle">
        <TextArea
          ref={inputRef}
          variant="unstyled"
          resizable="auto"
          rows={1}
          maxLength={UPDATE_SUMMARY_MAX_CHARS}
          defaultValue={shown.summary ?? ''}
          placeholder={t('update_summary_placeholder').toString()}
          aria-label={t('edit_update_summary').toString()}
          className={atoms({color: 'Text Subtle'})}
          style={{fontStyle: 'italic', width: '100%'}}
          onKeyDown={onKeyDown}
          onBlur={(event) => {
            if (skipBlurCommitRef.current) {
              skipBlurCommitRef.current = false;
              setEditing(false);
              return;
            }
            void commit(event.currentTarget.value);
          }}
        />
      </Box>
    );
  }

  if (shown.pending) return <SummaryPending size={quote ? undefined : 'small'} />;
  if (!shown.summary) return null;

  const line = quote ? (
    <UpdateSummaryQuote summary={shown.summary} edited={shown.edited} />
  ) : (
    <SummaryLine summary={shown.summary} edited={shown.edited} />
  );
  if (!editable) return line;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={t('edit_update_summary').toString()}
      className={styles.heroSummaryDisplay}
      // Keeps focus where it was, as the description does; the click opens it.
      onMouseDown={(event) => event.preventDefault()}
      onClick={startEditing}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          startEditing();
        }
      }}
    >
      {line}
    </div>
  );
};
